import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
  hasSetupFeePaid,
  getSubscriptionStatus,
  checkFeatureAccess,
  getUsageQuota,
  type UsageQuota,
} from '../services/subscriptionService';

export interface AccessControlState {
  hasFullAccess: boolean;
  needsSetupFee: boolean;
  needsSubscription: boolean;
  isAdmin: boolean;
  hasSetupFeePaid: boolean;
  hasActiveSubscription: boolean;
  subscriptionTier: string | null;
  subscriptionStatus: string | null;
  loading: boolean;
  error: Error | null;
}

export interface AccessControlActions {
  canAccessFeature: (featureKey: string) => Promise<boolean>;
  getQuota: (quotaType: 'ai_coach_messages' | 'job_match_analyses' | 'report_runs') => Promise<UsageQuota | null>;
  refresh: () => Promise<void>;
}

export interface AccessControlHook extends AccessControlState, AccessControlActions {}

const CACHE_DURATION = 5 * 60 * 1000;

interface CachedAccessControl {
  state: AccessControlState;
  timestamp: number;
  userId: string;
}

export function useAccessControl(): AccessControlHook {
  const { user, isAdmin } = useAuth();

  const [state, setState] = useState<AccessControlState>({
    hasFullAccess: false,
    needsSetupFee: true,
    needsSubscription: true,
    isAdmin: false,
    hasSetupFeePaid: false,
    hasActiveSubscription: false,
    subscriptionTier: null,
    subscriptionStatus: null,
    loading: true,
    error: null,
  });

  const cacheRef = useRef<CachedAccessControl | null>(null);
  const isMountedRef = useRef(true);

  const fetchAccessControl = useCallback(async (forceRefresh = false) => {
    if (!user) {
      setState({
        hasFullAccess: false,
        needsSetupFee: true,
        needsSubscription: true,
        isAdmin: false,
        hasSetupFeePaid: false,
        hasActiveSubscription: false,
        subscriptionTier: null,
        subscriptionStatus: null,
        loading: false,
        error: null,
      });
      return;
    }

    const now = Date.now();
    if (
      !forceRefresh &&
      cacheRef.current &&
      cacheRef.current.userId === user.id &&
      now - cacheRef.current.timestamp < CACHE_DURATION
    ) {
      setState(cacheRef.current.state);
      return;
    }

    try {
      setState(prev => ({ ...prev, loading: true, error: null }));

      if (isAdmin) {
        const adminState: AccessControlState = {
          hasFullAccess: true,
          needsSetupFee: false,
          needsSubscription: false,
          isAdmin: true,
          hasSetupFeePaid: true,
          hasActiveSubscription: true,
          subscriptionTier: 'admin',
          subscriptionStatus: 'active',
          loading: false,
          error: null,
        };

        cacheRef.current = {
          state: adminState,
          timestamp: now,
          userId: user.id,
        };

        if (isMountedRef.current) {
          setState(adminState);
        }
        return;
      }

      const [setupFeePaid, subscription] = await Promise.all([
        hasSetupFeePaid(user.id),
        getSubscriptionStatus(user.id),
      ]);

      const hasActiveSub = subscription?.subscription_status === 'active' ||
                          subscription?.subscription_status === 'trialing';

      const newState: AccessControlState = {
        hasFullAccess: setupFeePaid && hasActiveSub,
        needsSetupFee: !setupFeePaid,
        needsSubscription: setupFeePaid && !hasActiveSub,
        isAdmin: false,
        hasSetupFeePaid: setupFeePaid,
        hasActiveSubscription: hasActiveSub,
        subscriptionTier: subscription?.subscription_tier || null,
        subscriptionStatus: subscription?.subscription_status || null,
        loading: false,
        error: null,
      };

      cacheRef.current = {
        state: newState,
        timestamp: now,
        userId: user.id,
      };

      if (isMountedRef.current) {
        setState(newState);
      }
    } catch (err) {
      console.error('Error fetching access control:', err);
      const errorState = {
        ...state,
        loading: false,
        error: err instanceof Error ? err : new Error('Failed to fetch access control'),
      };

      if (isMountedRef.current) {
        setState(errorState);
      }
    }
  }, [user, isAdmin]);

  const canAccessFeature = useCallback(async (featureKey: string): Promise<boolean> => {
    if (!user) return false;

    if (isAdmin) return true;

    if (state.hasFullAccess) return true;

    try {
      return await checkFeatureAccess(user.id, featureKey);
    } catch (error) {
      console.error('Error checking feature access:', error);
      return false;
    }
  }, [user, isAdmin, state.hasFullAccess]);

  const getQuota = useCallback(async (
    quotaType: 'ai_coach_messages' | 'job_match_analyses' | 'report_runs'
  ): Promise<UsageQuota | null> => {
    if (!user) return null;

    if (isAdmin) {
      return {
        exists: true,
        used: 0,
        limit: -1,
        remaining: -1,
        percentage: 0,
        is_admin: true,
        unlimited: true,
      };
    }

    try {
      return await getUsageQuota(user.id, quotaType);
    } catch (error) {
      console.error('Error getting quota:', error);
      return null;
    }
  }, [user, isAdmin]);

  const refresh = useCallback(async () => {
    cacheRef.current = null;
    await fetchAccessControl(true);
  }, [fetchAccessControl]);

  useEffect(() => {
    isMountedRef.current = true;
    fetchAccessControl();

    return () => {
      isMountedRef.current = false;
    };
  }, [fetchAccessControl]);

  return {
    ...state,
    canAccessFeature,
    getQuota,
    refresh,
  };
}
